import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone, MessageCircle, ChevronRight, MapPin, Scale, Landmark, Gavel } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import SEO from "../components/SEO";
import { getLocationBySlug, getNearbyLocations, FIRM } from "../data/locations";
import { getLocalContext } from "../data/locationContext";
import { getLocationFaqs } from "../data/locationFaqs";
import { services } from "../data/services";
import InlineLeadForm from "../components/InlineLeadForm";

const SITE = "https://kandkassociates.com";

// Popular practice areas surfaced on every location page (with internal links).
const FEATURED_SLUGS = [
  "litigation-dispute-resolution", "intellectual-property", "arbitration",
  "matrimonial-disputes", "cheque-bouncing-disputes", "real-estate-property-laws",
  "consumer-protection-laws", "corporate-law-advisory", "debt-recovery",
  "motor-accident-claims", "succession-disputes", "landlord-tenant-disputes",
];

export default function LocationDetail() {
  const { slug } = useParams();
  const loc = getLocationBySlug(slug);
  if (!loc) return <Navigate to="/locations" replace />;

  const nearby = getNearbyLocations(slug, 6);
  const localContext = getLocalContext(slug);
  const featured = FEATURED_SLUGS.map((s) => services.find((x) => x.slug === s)).filter(Boolean);
  const isNcr = loc.zone === "Delhi NCR";

  const title = `Lawyers & Advocates in ${loc.name}`;
  const metaDescription = `Looking for experienced lawyers in ${loc.name}, ${loc.zone}? K & K Associates — Advocates & Solicitors — offers litigation, corporate, property, family, IP and consumer law services for clients in ${loc.name} (${loc.pincode}), with representation at the ${loc.court} and across Delhi NCR.`;

  const faqs = [
    { q: `Do you provide legal services in ${loc.name}?`, a: `Yes. K & K Associates serves individuals and businesses across ${loc.name} and the wider ${loc.zone} region. While our office is at ${FIRM.address}, we regularly act for clients from ${loc.name} (${loc.pincode}) and appear at the ${loc.court}${isNcr ? "" : ", the Delhi High Court and the Supreme Court of India"}. You can reach us on ${FIRM.phone} or WhatsApp to begin.` },
    { q: `Which court handles cases from ${loc.name}?`, a: `Most district-level matters arising in ${loc.name} are heard at the ${loc.court}. Depending on the nature and value of the dispute, matters may also go to the Delhi High Court, specialised tribunals (NCLT, DRT, NCDRC, MACT, Family Court) or, ultimately, the Supreme Court of India. We advise on the correct forum before filing.` },
    { q: `What types of lawyers do you have for ${loc.name} clients?`, a: `We are a full-service firm. For ${loc.name} clients we handle civil and commercial litigation, criminal defence, matrimonial and family matters, property and landlord-tenant disputes, cheque-bouncing cases, consumer complaints, debt recovery, intellectual property, corporate advisory and more — all under one roof.` },
    { q: `How do I book a consultation from ${loc.name}?`, a: `Call ${FIRM.phone}, WhatsApp us, email ${FIRM.email}, or use the contact form on our website. A partner reviews every enquiry and responds within one business day. We offer in-person consultations at our Defence Colony office as well as phone and video consultations for ${loc.name} clients.` },
    { q: `Do you handle urgent matters and interim relief for ${loc.name}?`, a: `Yes. We regularly file urgent applications — injunctions, stay orders, anticipatory bail and protection orders — for clients across ${loc.zone}. If your matter is time-sensitive, tell us when you call so we can prioritise it.` },
    { q: `What does a consultation for a ${loc.name} matter cost?`, a: `Fees are scoped during the first consultation and confirmed in a written engagement letter before any work begins. We offer fixed fees for one-shot matters and milestone-linked or capped retainers for ongoing litigation — so ${loc.name} clients always have cost predictability.` },
    ...getLocationFaqs(slug),
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LegalService",
        "@id": `${SITE}/locations/${loc.slug}#legalservice`,
        name: `${FIRM.name} — Advocates & Solicitors (serving ${loc.name})`,
        description: metaDescription,
        url: `${SITE}/locations/${loc.slug}`,
        telephone: FIRM.phoneIntl,
        email: FIRM.email,
        priceRange: "₹₹",
        address: {
          "@type": "PostalAddress",
          streetAddress: "A-197, LGF, Defence Colony",
          addressLocality: "New Delhi",
          postalCode: "110024",
          addressRegion: "Delhi",
          addressCountry: "IN",
        },
        geo: { "@type": "GeoCoordinates", latitude: 28.5722, longitude: 77.2305 },
        areaServed: { "@type": "Place", name: `${loc.name}, ${loc.zone}` },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE}/locations` },
          { "@type": "ListItem", position: 3, name: loc.name },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={title}
        description={metaDescription}
        canonical={`${SITE}/locations/${loc.slug}`}
        keywords={[
          `lawyers in ${loc.name}`, `advocates in ${loc.name}`, `law firm ${loc.name}`,
          `best lawyer ${loc.name}`, `legal services ${loc.name}`, `${loc.name} advocate`,
          `litigation lawyer ${loc.name}`, `property lawyer ${loc.name}`, `divorce lawyer ${loc.name}`,
        ]}
        schema={schema}
      />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-14 md:pb-20 bg-bone border-b border-rule" data-testid={`location-hero-${loc.slug}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <nav className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink/50 mb-10">
            <Link to="/" className="hover:text-brand">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/locations" className="hover:text-brand">Locations</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-brand">{loc.name}</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-9">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-7 h-7 text-brand" strokeWidth={1.25} />
                <p className="kicker">{loc.zone} · {loc.pincode}</p>
              </div>
              <h1 className="font-display text-4xl md:text-5xl xl:text-6xl leading-[1] text-ink">
                Lawyers &amp; Advocates in <span className="italic text-brand">{loc.name}</span>
              </h1>
              <p className="mt-8 font-serif text-xl md:text-2xl text-ink/75 leading-relaxed max-w-3xl">
                Full-service legal representation for individuals and businesses in {loc.name} — litigation, corporate, property, family, IP and consumer law, backed by a partner-led team.
              </p>
            </div>
            <div className="lg:col-span-3 flex flex-col gap-3">
              <Link to="/contact" className="btn-primary" data-testid="location-cta-consult">
                <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> Book Consultation
              </Link>
              <a href={`https://wa.me/${FIRM.whatsapp}`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="location-cta-whatsapp">
                <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-14 md:py-28 bg-paper">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <article className="font-serif text-lg md:text-xl text-ink/85 leading-[1.85]">
            <h2 className="kicker !text-brand mb-4">Trusted Legal Counsel for {loc.name}</h2>
            <p className="dropcap">
              Residents and businesses in {loc.name}, one of {loc.zone}'s well-known localities, increasingly seek a law firm that combines genuine courtroom experience with the discipline to keep clients out of unnecessary litigation. K & K Associates is that firm. Established in 2006, we act for individuals, founders, established corporations, financial institutions and overseas clients with matters connected to {loc.name} and the surrounding {loc.zone} area.
            </p>
            <p className="mt-6">
              Although our office is located at {FIRM.address}, distance is never a barrier for {loc.name} clients. We offer in-person, telephone and video consultations, handle filings and appearances on your behalf, and keep you updated at every stage. Matters arising in {loc.name} ({loc.pincode}) are typically heard at the {loc.court}{isNcr ? ", with appeals and higher matters carried to the relevant High Court and the Supreme Court of India through our network" : ", and we also appear at the Delhi High Court and the Supreme Court of India as the matter requires"}.
            </p>
            <p className="mt-6">
              {loc.name} is recognised by landmarks such as {loc.landmarks.join(", ")} — and like every neighbourhood, it generates its own pattern of legal needs: property and tenancy questions, family and matrimonial matters, recovery of dues, cheque-bounce complaints, consumer grievances, business and contractual disputes, and protection of brands and ideas. Whatever the matter, our partner-led team brings a single, consistent standard of care to every {loc.name} client.
            </p>
          </article>
        </div>

        {/* Local context — unique per location */}
        {localContext && (
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-12 md:mt-20" data-testid="location-local-context">
            <div className="bg-bone border border-rule p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Landmark className="w-6 h-6 text-brand" strokeWidth={1.25} />
                <p className="kicker">Legal Practice in {loc.name}</p>
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-6">
                Understanding {loc.name} — and its legal landscape.
              </h2>
              <p className="font-serif text-lg md:text-xl text-ink/80 leading-relaxed">{localContext}</p>
            </div>
          </div>
        )}

        {/* Practice areas in this location */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-14 md:mt-24">
          <div className="max-w-[1100px] mx-auto">
            <InlineLeadForm
              source={`Location: ${loc.name}`}
              heading={`Need a lawyer in ${loc.name}?`}
              subline={`Leave your name and number — a partner will call you back within one business day. We serve all of ${loc.zone}.`}
            />
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-8" data-testid="location-practice-areas-grid">
          <p className="kicker mb-5">Practice Areas in {loc.name}</p>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-12 max-w-3xl">
            The legal services we provide to {loc.name} clients.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-rule">
            {featured.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group border-r border-b border-rule p-7 bg-paper hover:bg-bone transition-colors"
                data-testid={`location-service-${s.slug}`}
              >
                <h3 className="font-display text-xl text-ink group-hover:text-brand transition-colors">
                  {s.title.split("—")[0].trim()} in {loc.name}
                </h3>
                <p className="mt-3 font-serif text-base text-ink/65 leading-snug">{s.short}</p>
              </Link>
            ))}
          </div>
          <Link to="/services" className="btn-outline mt-10 inline-flex" data-testid="location-all-services">
            View all 30 practice areas <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>

        {/* Courts & forums */}
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-14 md:mt-24">
          <div className="flex items-center gap-3 mb-6">
            <Gavel className="w-6 h-6 text-brand" strokeWidth={1.25} />
            <p className="kicker">Courts & Forums for {loc.name}</p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-8">
            Where {loc.name} matters are heard.
          </h2>
          <ul className="space-y-3 font-serif text-lg text-ink/80">
            {[
              `${loc.court} — the primary district forum for civil, criminal and family matters arising in ${loc.name}`,
              isNcr ? "The relevant High Court for writs, appeals and commercial matters" : "Delhi High Court — writ jurisdiction, commercial division, appeals and IP matters",
              "Specialised tribunals — NCLT, DRT/DRAT, NCDRC and State Consumer Commission, MACT and Family Courts",
              "Supreme Court of India — SLPs, civil and criminal appeals through our Advocate-on-Record network",
            ].map((c) => (
              <li key={c} className="flex gap-3"><Check className="w-5 h-5 text-brand mt-1.5 shrink-0" strokeWidth={1.5} /><span>{c}</span></li>
            ))}
          </ul>
        </div>

        {/* Why choose us here */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-14 md:mt-24">
          <div className="bg-bone border-l-4 border-brand p-10 md:p-14">
            <p className="kicker mb-5">Why {loc.name} Clients Choose K &amp; K</p>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-8">
              A firm built on restraint, not volume.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
              {[
                "Partner-led attention on every matter — not a delegated junior",
                "A 'how not to litigate' philosophy that saves time, money and stress",
                "Transparent, milestone-linked fees with no surprises",
                "Full-service bench — civil, criminal, family, corporate, IP and property",
                "In-person, phone and video consultations convenient for " + loc.name,
                "Representation from the first notice to final enforcement",
              ].map((w) => (
                <div key={w} className="flex gap-3 font-serif text-lg text-ink/80">
                  <Check className="w-5 h-5 text-brand mt-1.5 shrink-0" strokeWidth={1.5} /><span>{w}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-14 md:mt-24">
          <p className="kicker mb-4">Frequently Asked</p>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-10">
            Questions {loc.name} clients ask us.
          </h2>
          <Accordion type="single" collapsible className="border-t border-rule">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-rule">
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-ink hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-serif text-lg text-ink/75 leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* GEO callout */}
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-12 md:mt-20">
          <div className="border border-rule p-8 md:p-10 bg-bone">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-5 h-5 text-brand" strokeWidth={1.5} />
              <p className="kicker">Serving {loc.name} & All of Delhi NCR</p>
            </div>
            <p className="font-serif text-lg text-ink/80 leading-relaxed">
              From our base at {FIRM.address}, K & K Associates serves {loc.name} and every other locality across Delhi NCR — Gurugram, Noida, Faridabad and Ghaziabad — and pan-India through a vetted local-counsel network. Call <a href={`tel:${FIRM.phone.replace(/[^0-9]/g, "")}`} className="text-brand link-underline">{FIRM.phone}</a> or WhatsApp <a href={`https://wa.me/${FIRM.whatsapp}`} target="_blank" rel="noreferrer" className="text-brand link-underline">+91 96435 72870</a>.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-12 md:mt-20">
          <div className="bg-ink text-white p-10 md:p-14">
            <p className="kicker text-white/60 mb-5">Begin a Conversation</p>
            <h3 className="font-display text-3xl md:text-4xl leading-tight">
              Need a lawyer in <span className="italic text-white/70">{loc.name}?</span>
            </h3>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-ghost-light" data-testid="location-bottom-cta-contact">
                Book Consultation <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
              <a href={`tel:${FIRM.phone.replace(/[^0-9]/g, "")}`} className="btn-primary border-white" data-testid="location-bottom-cta-call">
                <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {FIRM.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby locations interlink */}
      {nearby.length > 0 && (
        <section className="bg-bone border-t border-rule py-14 md:py-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <p className="kicker mb-5">Lawyers in Nearby Areas</p>
            <h2 className="font-display text-3xl md:text-4xl text-ink mb-12">
              We also serve these neighbourhoods.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-rule">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to={`/locations/${n.slug}`}
                  className="group border-r border-b border-rule p-7 bg-paper hover:bg-bone transition-colors flex items-center justify-between"
                  data-testid={`nearby-location-${n.slug}`}
                >
                  <span>
                    <span className="font-display text-xl text-ink group-hover:text-brand transition-colors">Lawyers in {n.name}</span>
                    <span className="block kicker text-ink/40 mt-2">{n.zone}</span>
                  </span>
                  <ArrowRight className="w-5 h-5 text-ink/30 group-hover:text-brand group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </Link>
              ))}
            </div>
            <Link to="/locations" className="btn-outline mt-10 inline-flex" data-testid="location-all-locations">
              View all 100 locations <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        </section>
      )}
    </>
  );
}
